import { Element, Component, h, Host, Prop, Listen, State } from '@stencil/core';
import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';

import { Environment, ErrorCorrectionLevel, ThemePreset } from './enums';
import { Logger } from '../../utils/utils';
import * as utilsURL from '../../utils/url';
import Logo from './logo';
import { WS } from './websocket';

const maxInterval = 59 * 60 * 1000;
const minInterval = 10 * 1000;

const DEFAULT_CALLBACK_URL = window.location.origin + '/zero/auth';
const ANIMATION_DURATION = 3000

@Component({
  tag: 'zero-account',
  styleUrl: 'zero-account.css',
  shadow: true,
})
export class ZeroAccount {
  @Prop() appId!: string;
  @Prop() updateInterval?: number = 3 * 60 * 1000;
  @Prop({attribute: 'callback-url'}) callbackURL: string = DEFAULT_CALLBACK_URL;
  @Prop() level?: ErrorCorrectionLevel = ErrorCorrectionLevel.M;
  @Prop() environment?: Environment = Environment.production;
  @Prop() enableWithCredentials?: boolean = false;
  @Prop() themePreset?: ThemePreset = ThemePreset.light;
  @Prop() autoStartApp? = true;

  private logger: Logger;
  private uaParser: UAParser;
  private ws: WS;

  private timer: number;
  private animationTimer: number;

  @Element() host: HTMLElement;
  private modalEl: HTMLDivElement;
  private closeButtonEl: HTMLDivElement;

  @State() modalOpen = false;
  @State() isOpenAppBeingClicked = false;
  @State() isAnimating = false;
  @State() url = '';

  private getUpdateInterval() {
    // make sure the provided value is within bounds
    return Math.min(Math.max(this.updateInterval, minInterval), maxInterval);
  }
  private refreshQR = (ignoreAnimate = false)=> {
    this.initServices();
    const update = () => {
      this.restartTimer();
      this.updateURL()
    }
    if (ignoreAnimate) return update();

    this.isAnimating = true
    this.animationTimer = window.setTimeout(()=>{
      update()
      this.isAnimating = false
    }, ANIMATION_DURATION)
  }

  @Listen('0account-toggle-modal', { target: 'document' })
  toggleModal() {
    this.modalOpen = !this.modalOpen;
    if (this.modalOpen) this.refreshQR(true)
    if ((this.modalOpen && this.autoStartApp) || this.isMobile()) this.openApp()
  }

  @Listen('0account-close-modal', { target: 'document' })
  @Listen('0account-authenticated', { target: 'document' })
  private _closeModal() {
    this.modalOpen = false;
    this.stopTimer()
  }

  @Listen('0account-logout', { target: 'document' })
  logout() {
    this.ws.logout()
  }

  private closeModal(e: MouseEvent) {
    if (e.target !== this.modalEl && e.target !== this.closeButtonEl) {
      return e.stopPropagation();
    }
    this._closeModal()
  }

  @Listen('0account-open-app', { target: 'document' })
  openApp() {
      this.refreshQR(true)

      // TODO: uncomment if the fix with isOpenAppClicked does not work
      // const confirmBeforeClose = (event) => {
      //   event = event || window.event;
      //   if (!event) return;
      //   event.preventDefault();
      //   event.returnValue = '';
      //   delete event['returnValue'];
      //   return '';
      // }
      //
      // window.addEventListener('beforeunload', confirmBeforeClose);

      if (this.isOpenAppBeingClicked) return;
      this.isOpenAppBeingClicked = true;
      window.location.href = this.url;

      setTimeout(()=>{
        // window.removeEventListener('beforeunload', confirmBeforeClose);
        this.isOpenAppBeingClicked = false;
      }, 300)
  }

  render() {
    const logoColor = this.themePreset === ThemePreset.light ? '#7263FA': '#FFFFF9'
    const qrColor = '#000'
    return (
      <Host>
        <slot />
        {this.host.childNodes.length === 0 &&
          <button class={`button ${this.themePreset} ${this.isOpenAppBeingClicked ? 'disabled':''}`} onClick={()=>this.toggleModal()}>
            <Logo className='button__logo' color={logoColor} />
            use 0account
          </button>
        }
        <div
          ref={el => this.modalEl = el}
          onClick={(e) => this.closeModal(e)}
          class={`modal ${this.themePreset} ${this.isAnimating ? 'modal-animated': ''} ${this.modalOpen ? '' : 'modal--hidden'}`}>
          <div class='modal__content'>
            <div ref={el => this.closeButtonEl = el} class='modal__close-button'></div>
            <div class='modal__qrcode__container'>
              <qr-code url={this.url}  class="modal__qrcode" level={this.level} color={qrColor}/>
              <div class='circle__overlay'>
                <div class='circle'></div>
                <div class='circle circle--medium'></div>
                <div class='circle circle--large'></div>
              </div>
              <Logo className='modal__logo' color={logoColor} />
            </div>
            <div class='modal__buttons'>
              <button disabled={this.isAnimating || this.isOpenAppBeingClicked} onClick={()=>this.openApp()} class='modal__button'>Open 0account</button>
              <a href='https://0account.com/downloads' class='modal__link'>Download</a>
            </div>
          </div>
        </div>
      </Host>
    );
  }

  private hideOnEsc = (e) => {
    if ((e.key === 'Escape' || e.key === 'Esc')) {
      this.modalOpen = false;
    }
  };

  initServices() {
    if (this.logger && this.uaParser && this.ws) return;
    this.logger = new Logger(this.environment);
    this.uaParser = new UAParser()
    this.ws = new WS(this.appId, {
      updateInterval: this.getUpdateInterval(),
      enableWithCredentials: this.enableWithCredentials,
      callbackURL: this.callbackURL,
      uaParser: this.uaParser,
      env: this.environment,
      log: this.log,
    })
  }
  // called only once
  componentWillLoad() {
    this.initServices()

    // make sure we initQR
    this.refreshQR(true)
    document.addEventListener('keydown', this.hideOnEsc);
  }

  private updateURL() {
    const expires = new Date().getTime() + this.getUpdateInterval();
    const uuid = uuidv4();
    this.ws.updateUUID(uuid);
    const params = utilsURL.appendToParams('', {
      appId: this.appId,
      uuid: uuid,
      expires: expires,
      domain: window.location.hostname,
    })

    if (this.isMobile()) {
      this.url = 'https://launch.0account.com/auth?' + params;
    } else {
      this.url = 'zero-account://auth?' + params;
    }
  }

  isMobile() {
    const device = this.uaParser.getDevice()
    return device.type === 'tablet' || device.type === 'mobile'
  }

  connectedCallback() {
    this.startTimer()
  }

  disconnectedCallback() {
    this.stopTimer()
    document.removeEventListener('keydown', this.hideOnEsc);
  }

  private restartTimer() {
    this.stopTimer()
    this.startTimer()
  }

  private startTimer() {
    this.timer = window.setInterval(this.refreshQR, this.getUpdateInterval());
  }

  private stopTimer() {
    window.clearInterval(this.timer);
    window.clearTimeout(this.animationTimer);
    this.isAnimating = false
  }

  private log = (...args) => this.logger.log(...args);
}
